import { Key, useMemo } from 'react';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Chip,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Tab,
  Tabs,
  Tooltip,
  useDisclosure,
} from '@nextui-org/react';
import InfoButton from '../info/InfoButton';
import SettingsButton from '../settings/SettingsButton';
import SettingsModal from '../settings/SettingsModal';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectDisplayName,
  selectUserRole,
  selectUserRoleLabel,
} from '../../store/auth/authSlice';
import { refreshWebsocket, selectConnected } from '../../store/chat/chatSlice';
import { FaCircleUser, FaRotateRight } from 'react-icons/fa6';
import { selectAppMode, setAppMode } from '../../store/settings/settingsSlice';
import { APP } from '../../util';
import { selectCurrentClient } from '../../store/client/clientSlice';
import { GENERIC_APP } from '../../config/generic';
import { SOS_APP } from '../../config/sos';
import { Image } from '@nextui-org/react';
import UsersButton from '../user/UsersButton';
import { useNavigate } from 'react-router-dom';
import ButtonWithConfirmModal from './ButtonWithConfirmModal';
import { logout } from '../../store/auth/authThunks';
import { FaArrowRightFromBracket } from 'react-icons/fa6';
import Icon from '../base/Icon';
import SavedChatsButton from '../saved-chats/SavedChatsButton';
import ApiManagerButton from '../api/ApiManagerButton';

export default function NavBar(props: { page: string }) {
  const page = props.page;
  const dispatch = useAppDispatch();
  const displayName = useAppSelector(selectDisplayName);
  const userRole = useAppSelector(selectUserRole);
  const userRoleLabel = useAppSelector(selectUserRoleLabel);
  const isConnected = useAppSelector(selectConnected);
  const appMode = useAppSelector(selectAppMode);
  const currentClient = useAppSelector(selectCurrentClient);
  const {
    isOpen: isSettingsOpen,
    onOpen: onSettingsOpen,
    onOpenChange: onSettingsOpenChange,
  } = useDisclosure();

  const showDialogueTab = useMemo(
    () => userRole !== 'user_lets_talk',
    [userRole]
  );

  const showLetsTalkTab = useMemo(
    () =>
      process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC' &&
      (userRole === 'admin' || userRole === 'user_lets_talk'),
    [userRole]
  );

  const showUsersTab = useMemo(() => userRole === 'admin', [userRole]);

  const isTrainee = useMemo(() => userRole === 'user_trainee', [userRole]);

  const showSettingsTab = !isTrainee;

  const showLogout = isTrainee;

  const showClientTab = currentClient !== undefined && !isTrainee;

  const showSavedChatsTab = useMemo(
    () => userRole === 'admin' || userRole === 'user_sos',
    [userRole]
  );

  const showApiManagerTab = useMemo(() => userRole === 'admin', [userRole]);

  const handleRefresh = () => {
    dispatch(refreshWebsocket());
  };

  const navigate = useNavigate();
  const handleSetAppMode = (key: Key) => {
    // @ts-ignore: key is always of type AppMode
    dispatch(setAppMode(key));
    if (page === 'users' || page === 'chats') {
      navigate('/');
    }
  };

  const appVersion = process.env.REACT_APP_VERSION
    ? `v${process.env.REACT_APP_VERSION}`
    : undefined;

  const name =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? GENERIC_APP.name
      : SOS_APP.name;

  const logo =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? GENERIC_APP.logo
      : SOS_APP.logo;

  const appTitle = <p className="text-lg font-bold">{name}</p>;

  const handleLogout = () => {
    dispatch(logout());
  };

  const connectionIndicator = isConnected ? (
    <Chip color="success" variant="flat">
      Online
    </Chip>
  ) : (
    <Tooltip
      content={
        <Button
          size="sm"
          variant="light"
          startContent={<Icon icon={FaRotateRight} />}
          onPress={handleRefresh}
        >
          Reconnect
        </Button>
      }
      className="p-0 overflow-hidden"
    >
      <Chip color="danger" variant="flat">
        Offline
      </Chip>
    </Tooltip>
  );

  const userGroup = displayName ? (
    <Badge
      isInvisible={!userRoleLabel}
      content={userRoleLabel?.tag}
      size="sm"
      color={userRoleLabel?.color}
      shape="rectangle"
      variant="faded"
      placement="bottom-right"
      className="translate-x-1/4 translate-y-2/3"
    >
      <Chip startContent={<Icon icon={FaCircleUser} className="w-5 h-5" />}>
        {displayName}
      </Chip>
    </Badge>
  ) : undefined;

  const modeTabs = (
    <Tabs
      aria-label="App mode"
      radius="full"
      color="primary"
      selectedKey={appMode}
      onSelectionChange={handleSetAppMode}
      classNames={{ tabList: 'shadow-small' }}
    >
      {showDialogueTab && <Tab key="dialogue" title={APP.TITLE.SHORT} />}
      {showLetsTalkTab && <Tab key="letstalk" title="Let's Talk" />}
      {showClientTab && <Tab key="client-profile" title="Client Profile" />}
    </Tabs>
  );

  return (
    <Navbar
      height="5rem"
      className="pt-4 bg-transparent"
      classNames={{ wrapper: 'max-w-full px-4' }}
    >
      <Card className="flex flex-row w-full h-full px-6 py-2">
        <NavbarBrand className="flex flex-row gap-4">
          <NavbarItem>
            {appVersion ? (
              <Badge
                content={appVersion}
                size="sm"
                color="primary"
                shape="rectangle"
                placement="bottom-right"
                className="translate-x-1/4 translate-y-3/4 shadow-md"
              >
                {appTitle}
              </Badge>
            ) : (
              appTitle
            )}
          </NavbarItem>
          <NavbarItem>{connectionIndicator}</NavbarItem>
        </NavbarBrand>
        <NavbarContent justify="center">
          <NavbarItem>{modeTabs}</NavbarItem>
        </NavbarContent>
        <NavbarContent justify="end">
          <NavbarItem>
            <Image src={logo} className="h-12 mx-2 app-logo" />
          </NavbarItem>
          {userGroup && <NavbarItem>{userGroup}</NavbarItem>}
          <NavbarItem>
            <ButtonGroup color="primary" radius="full" variant="flat">
              {showUsersTab && <UsersButton />}
              {showSavedChatsTab && <SavedChatsButton />}
              <InfoButton />
              {showSettingsTab && <SettingsButton onPress={onSettingsOpen} />}
              {showApiManagerTab && <ApiManagerButton />}
              {showLogout && (
                <ButtonWithConfirmModal
                  onPress={handleLogout}
                  buttonProps={{
                    isIconOnly: true,
                  }}
                  buttonContent={<Icon icon={FaArrowRightFromBracket} />}
                  confirmLabel="Logout"
                  modalHeader="Logout?"
                />
              )}
            </ButtonGroup>
            <SettingsModal
              isOpen={isSettingsOpen}
              onOpenChange={onSettingsOpenChange}
            />
          </NavbarItem>
        </NavbarContent>
      </Card>
    </Navbar>
  );
}
